import { useMemo } from 'react'
import entries from 'lodash/entries'
import flatMap from 'lodash/fp/flatMap'
import getOr from 'lodash/fp/getOr'
import groupBy from 'lodash/fp/groupBy'
import keyBy from 'lodash/fp/keyBy'
import map from 'lodash/fp/map'
import mapValues from 'lodash/fp/mapValues'
import pipe from 'lodash/fp/pipe'
import sortBy from 'lodash/fp/sortBy'
import { parse as parseDate } from 'lib/family-cup-utils/event-dates'
import { mapReduce } from 'lib/utils/array'
import { orderBy } from 'lodash'

const buildRank = (rank, { tied = false } = {}) =>
  tied ? `T-${rank}` : rank.toString()

const winningRanks = [buildRank(1), buildRank(1, { tied: true })]

export const isWinner = ({ rank }) => winningRanks.includes(rank)

const flatMapWithIndex = flatMap.convert({ cap: false })
const sortByEventDate = sortBy(event => parseDate(event.date))
const sortByIncreasingTotal = orderBy(([total]) => total)

const tallyScores = (teamResults, sortedEvents, team) =>
  mapReduce(
    sortedEvents,
    (acc, event) => {
      const score = teamResults[team.id]?.[event.id]?.score || 0
      return [score, score + acc]
    },
    0,
  )

const buildTeamWithScores = (teamResults, sortedEvents) => team => {
  const [scores, total] = tallyScores(teamResults, sortedEvents, team)
  return { scores, total, name: team.name }
}

function addRank([_, teams], index) {
  const rank = buildRank(index + 1, { tied: teams.length > 1 })
  return teams.map(team => ({ ...team, rank }))
}

export function buildStandings(program, events) {
  const sortedEvents = sortByEventDate(events)

  const teamResults = pipe(
    flatMap(getOr([], 'teamResults')),
    groupBy('teamId'),
    mapValues(keyBy('eventId')),
  )(sortedEvents)

  return pipe(
    map(buildTeamWithScores(teamResults, sortedEvents)),
    groupBy('total'),
    entries,
    sortByIncreasingTotal,
    flatMapWithIndex(addRank),
  )(program.teams)
}

export const useStandings = (program, events) =>
  useMemo(() => buildStandings(program, events), [program, events])
